import React, { useEffect, useMemo, useState } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { resolveTauriMediaUrl } from './fetch-wrapper';

type TauriPlayerProps = ReactPlayerProps & {
  // Keep the same API as ReactPlayer; we will only override url behavior
};

// Detect Tauri environment
const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;

function getMimeTypeFromUrl(url: string): string | undefined {
  const extension = url
    .split('?')[0]
    .split('#')[0]
    .split('.')
    .pop()
    ?.toLowerCase();
  switch (extension) {
    // Video
    case 'mp4':
      return 'video/mp4';
    case 'webm':
      return 'video/webm';
    case 'ogv':
      return 'video/ogg';
    case 'mov':
      return 'video/quicktime';
    case 'mkv':
      return 'video/x-matroska';
    case 'm4v':
      return 'video/x-m4v';
    case 'avi':
      return 'video/x-msvideo';
    case 'flv':
      return 'video/x-flv';
    // Audio
    case 'mp3':
      return 'audio/mpeg';
    case 'm4a':
      return 'audio/mp4';
    case 'aac':
      return 'audio/aac';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
    case 'oga':
      return 'audio/ogg';
    case 'flac':
      return 'audio/flac';
    case 'opus':
      return 'audio/opus';
    default:
      return undefined;
  }
}

function normalizeToSingleUrl(
  url: ReactPlayerProps['url']
): string | undefined {
  if (!url) return undefined;
  if (typeof url === 'string') return url;
  if (Array.isArray(url)) {
    // If array of strings or sources, pick the first
    const first = url[0] as any;
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && typeof first.src === 'string') {
      return first.src as string;
    }
  }
  if (typeof (url as any)?.src === 'string') return (url as any).src as string;
  return undefined;
}

export const TauriPlayer: React.FC<TauriPlayerProps> = (props) => {
  const { url, ...rest } = props;
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(undefined);

  const inputUrl = useMemo(() => normalizeToSingleUrl(url), [url]);

  useEffect(() => {
    let isMounted = true;
    let createdBlobUrl: string | undefined;

    async function load() {
      // Case 1: url is an ArrayBuffer or Uint8Array (e.g., Tauri prepared data)
      if (url instanceof ArrayBuffer || url instanceof Uint8Array) {
        const blob = new Blob([
          url instanceof Uint8Array ? url : new Uint8Array(url),
        ]);
        const blobUrl = URL.createObjectURL(blob);
        // Assign immediately so cleanup can revoke it even if unmounted
        createdBlobUrl = blobUrl;
        if (!isMounted) {
          // Component unmounted, cleanup will handle revocation
          return;
        }
        setResolvedUrl(blobUrl);
        return;
      }

      // Case 2: url can be normalized to a string URL
      if (inputUrl) {
        // In web, just use the direct URL
        if (!isTauriEnv) {
          if (isMounted) {
            setResolvedUrl(inputUrl);
          }
          return;
        }

        try {
          const mediaUrl = await resolveTauriMediaUrl(
            inputUrl,
            getMimeTypeFromUrl(inputUrl) || 'application/octet-stream'
          );
          // Assign blob URL immediately for cleanup before checking mounted state
          if (mediaUrl.startsWith('blob:')) {
            createdBlobUrl = mediaUrl;
          }
          if (!isMounted) {
            // Component unmounted, cleanup will handle revocation
            return;
          }
          setResolvedUrl(mediaUrl);
        } catch (e) {
          // Fallback: pass through original URL
          if (!isMounted) return;
          setResolvedUrl(inputUrl);
        }
        return;
      }

      // Otherwise, nothing usable
      if (isMounted) {
        setResolvedUrl(undefined);
      }
    }

    load();

    return () => {
      isMounted = false;
      // If we produced a blob: URL, revoke it using the local createdBlobUrl
      if (createdBlobUrl && createdBlobUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(createdBlobUrl);
        } catch {}
      }
    };
  }, [inputUrl, url]);

  return <ReactPlayer url={resolvedUrl} {...rest} />;
};

export default TauriPlayer;
